import React, { useEffect, useState } from "react";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  TextField,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import dayjs from "dayjs";
import { useStyles } from "../../theme/appStyles";

interface TimeDropdownProps {
  value: string | undefined; // e.g., "14:30" or ""
  onChange: (time: string | undefined) => void;
  size?: "small" | "medium";
}

const TimeDropdown: React.FC<TimeDropdownProps> = ({
  value,
  onChange,
  size = "small",
}) => {
  const classes = useStyles();
  const parsedTime =
    value && dayjs(value, "HH:mm", true).isValid()
      ? dayjs(value, "HH:mm")
      : null;

  const [hour, setHour] = useState<number | undefined>(
    parsedTime ? parsedTime.hour() % 12 || 12 : undefined
  );
  const [minute, setMinute] = useState<number | undefined>(
    parsedTime ? parsedTime.minute() : undefined
  );
  const [ampm, setAmPm] = useState<"AM" | "PM" | undefined>(
    parsedTime ? (parsedTime.hour() >= 12 ? "PM" : "AM") : undefined
  );

  // Sync from outside when value changes
  useEffect(() => {
    if (parsedTime) {
      setHour(parsedTime.hour() % 12 || 12);
      setMinute(parsedTime.minute());
      setAmPm(parsedTime.hour() >= 12 ? "PM" : "AM");
    } else {
      setHour(undefined);
      setMinute(undefined);
      setAmPm(undefined);
    }
  }, [value]);

  // Generate combined value
  useEffect(() => {
    if (hour !== undefined && minute !== undefined && ampm !== undefined) {
      let h =
        ampm === "PM" ? (hour !== 12 ? hour + 12 : 12) : hour === 12 ? 0 : hour;
      const formatted = dayjs().hour(h).minute(minute).format("HH:mm");
      onChange(formatted);
    } else {
      onChange(undefined);
    }
  }, [hour, minute, ampm]);

  return (
    <Box display="flex" gap={1} alignItems="center">
      <FormControl size={size} sx={{ width: 90 }}>
        <InputLabel>Hour</InputLabel>
        <Select
          value={hour !== undefined ? hour.toString() : ""}
          label="Hour"
          onChange={(e: SelectChangeEvent<string>) => {
            const val = e.target.value;
            setHour(val ? parseInt(val, 10) : undefined);
          }}
          // displayEmpty
        >
          <MenuItem value="">--</MenuItem>
          {[...Array(12)].map((_, i) => {
            const hr = i + 1;
            return (
              <MenuItem key={hr} value={hr.toString()}>
                {hr.toString().padStart(2, "0")}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>

      <FormControl size={size} sx={{ width: 90 }}>
        <InputLabel>Mini</InputLabel>
        <Select
          value={minute !== undefined ? minute.toString() : ""}
          label="Minute"
          onChange={(e: SelectChangeEvent<string>) => {
            const val = e.target.value;
            setMinute(val ? parseInt(val, 10) : undefined);
          }}
        >
          <MenuItem value="">--</MenuItem>
          {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map((m) => (
            <MenuItem key={m} value={m.toString()}>
              {m.toString().padStart(2, "0")}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size={size} sx={{ width: 120 }}>
        <InputLabel>AM/PM</InputLabel>
        <Select
          value={ampm ?? ""}
          label="AM/PM"
          onChange={(e: SelectChangeEvent<string>) => {
            const val = e.target.value;
            setAmPm(val === "AM" || val === "PM" ? val : undefined);
          }}
        >
          <MenuItem value="">--</MenuItem>
          <MenuItem value="AM">AM</MenuItem>
          <MenuItem value="PM">PM</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );
};

export default TimeDropdown;
